import { FormInstance } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { NouveauDossierConcessionnaireData } from 'types/Concessionnaire';

interface UseStep1PersonneMoraleFlowArgs {
  form: FormInstance<NouveauDossierConcessionnaireData>;
  personnePhysique: boolean;
  etablissementSectionFields: (keyof NouveauDossierConcessionnaireData)[];
  coordonneesSectionFields: (keyof NouveauDossierConcessionnaireData)[];
}

export default function useStep1PersonneMoraleFlow({
  form,
  personnePhysique,
  etablissementSectionFields,
  coordonneesSectionFields,
}: UseStep1PersonneMoraleFlowArgs) {
  const [isEtablissementSectionOpen, setIsEtablissementSectionOpen] =
    useState(true);
  const [isCoordonneesSectionOpen, setIsCoordonneesSectionOpen] =
    useState(false);
  const [isEtablissementSectionSaved, setIsEtablissementSectionSaved] =
    useState(false);
  const [isCoordonneesSectionSaved, setIsCoordonneesSectionSaved] =
    useState(false);

  useEffect(() => {
    if (personnePhysique) {
      setIsEtablissementSectionOpen(true);
      setIsCoordonneesSectionOpen(true);
      setIsEtablissementSectionSaved(true);
      setIsCoordonneesSectionSaved(true);
      return;
    }

    setIsEtablissementSectionOpen(true);
    setIsCoordonneesSectionOpen(false);
    setIsEtablissementSectionSaved(false);
    setIsCoordonneesSectionSaved(false);
  }, [personnePhysique]);

  const handleSaveEtablissementSection = useCallback(async () => {
    try {
      await form.validateFields(etablissementSectionFields as string[]);
    } catch {
      return;
    }

    setIsEtablissementSectionSaved(true);
    setIsEtablissementSectionOpen(false);
    setIsCoordonneesSectionOpen(true);
  }, [etablissementSectionFields, form]);

  const handleSaveCoordonneesSection = useCallback(async () => {
    try {
      await form.validateFields(coordonneesSectionFields as string[]);
    } catch {
      return;
    }

    setIsCoordonneesSectionSaved(true);
    setIsCoordonneesSectionOpen(false);
  }, [coordonneesSectionFields, form]);

  const handleValuesChange = useCallback(
    (changedValues: Partial<NouveauDossierConcessionnaireData>) => {
      if (personnePhysique) {
        return;
      }

      const changedKeys = Object.keys(
        changedValues,
      ) as (keyof NouveauDossierConcessionnaireData)[];

      const etablissementTouched = changedKeys.some((key) =>
        etablissementSectionFields.includes(key),
      );
      const coordonneesTouched = changedKeys.some((key) =>
        coordonneesSectionFields.includes(key),
      );

      if (etablissementTouched && isEtablissementSectionSaved) {
        setIsEtablissementSectionSaved(false);
        setIsCoordonneesSectionSaved(false);
        setIsEtablissementSectionOpen(true);
        setIsCoordonneesSectionOpen(false);
      }

      if (coordonneesTouched && isCoordonneesSectionSaved) {
        setIsCoordonneesSectionSaved(false);
      }
    },
    [
      coordonneesSectionFields,
      etablissementSectionFields,
      isCoordonneesSectionSaved,
      isEtablissementSectionSaved,
      personnePhysique,
    ],
  );

  const toggleEtablissementSection = useCallback(() => {
    setIsEtablissementSectionOpen((previous) => !previous);
  }, []);

  const toggleCoordonneesSection = useCallback(() => {
    setIsCoordonneesSectionOpen((previous) => !previous);
  }, []);

  return {
    isEtablissementSectionOpen,
    isCoordonneesSectionOpen,
    isEtablissementSectionSaved,
    isCoordonneesSectionSaved,
    handleSaveEtablissementSection,
    handleSaveCoordonneesSection,
    handleValuesChange,
    toggleEtablissementSection,
    toggleCoordonneesSection,
  };
}
